import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell, Cog, Trash2, Inbox, PackageCheck, CheckCheck,
  FileText, Truck, ClipboardCheck, AlertTriangle, Briefcase, ArrowRightLeft, Undo2,
} from "lucide-react";
import api, { apiErr } from "../lib/api";

const POLL_MS = 30000;

const KIND_META = {
  // ---- Staff-facing ----
  parts_request_new: {
    icon: Cog,
    title: (p) => `Parts requested for ${p.delivery_note_number || "job"}`,
    body: (p) => p.part_description,
    path: "/app/parts",
    tone: "amber",
  },
  parts_received: {
    icon: PackageCheck,
    title: () => `Parts received — back in lab`,
    body: (p) => `${p.part_description || ""} · ${p.delivery_note_number || ""}`.trim(),
    path: "/app/worklist",
    tone: "green",
  },
  equipment_scrapped: {
    icon: Trash2,
    title: () => `Equipment flagged unserviceable`,
    body: (p) => `${p.description || "Item"} · ${p.customer_name || ""}`.trim(),
    path: "/app/scrapped",
    tone: "red",
  },
  unserviceable_review_needed: {
    icon: AlertTriangle,
    title: (p) => `Unserviceable review · ${p.delivery_note_number || "Job"}`,
    body: (p) => `${p.description || "Item"} · ${p.reason || ""}`.replace(/\s·\s$/, ""),
    path: "/app/scrapped",
    tone: "amber",
  },
  rfq_submitted: {
    icon: Inbox,
    title: (p) => `New RFQ ${p.reference || ""}`.trim(),
    body: (p) => `${p.customer_name || "Customer"} · ${p.item_count || 0} item(s)`,
    path: "/app/rfqs",
    tone: "blue",
  },
  form_signed: {
    icon: FileText,
    title: (p) => `${p.form_title || "Form"} signed`,
    body: (p) => `${p.customer_name || "Customer"}${p.customer_number ? ` · ${p.customer_number}` : ""}`,
    path: "/app/forms",
    tone: "green",
  },
  equipment_added_to_job: {
    icon: Briefcase,
    title: (p) => `Equipment added to DN ${p.delivery_note_number || ""}`.trim(),
    body: (p) => `${p.description || "Item"}${p.serial_number ? ` · ${p.serial_number}` : ""} · ${p.customer_name || ""}${p.reused_existing_equipment ? " (from register)" : ""}`,
    path: "/app/active-lab",
    tone: "blue",
  },
  equipment_transferred_job: {
    icon: ArrowRightLeft,
    title: (p) => `Equipment moved: DN ${p.from_delivery_note_number || "—"} → DN ${p.to_delivery_note_number || "—"}`,
    body: (p) => `${p.description || "Item"}${p.serial_number ? ` · ${p.serial_number}` : ""} · ${p.customer_name || ""}`,
    path: "/app/active-lab",
    tone: "amber",
  },
  return_to_customer_requested: {
    icon: Undo2,
    title: (p) => `Return to Customer · DN ${p.delivery_note_number || "Job"}`,
    body: (p) => `${p.description || "Item"} · ${p.customer_name || ""}${p.reason ? ` — ${p.reason}` : ""}`,
    path: "/app/job-flow",
    tone: "blue",
  },
  // ---- Customer-facing ----
  rfq_quoted: {
    icon: FileText,
    title: (p) => `Your quote is ready — ${p.reference || "RFQ"}`,
    body: (p) => p.quote_number ? `Quote ${p.quote_number}` : "Quote available — please review",
    path: "/app/request-quote",
    tone: "blue",
  },
  rfq_cancelled: {
    icon: FileText,
    title: (p) => `RFQ ${p.reference || ""} was cancelled`,
    body: () => "Please contact us if this was unexpected",
    path: "/app/request-quote",
    tone: "red",
  },
  rfq_converted: {
    icon: ClipboardCheck,
    title: (p) => `Your RFQ ${p.reference || ""} is now a job`,
    body: (p) => `Delivery note ${p.delivery_note_number || ""}`,
    path: "/app/history",
    tone: "green",
  },
  equipment_ready_for_delivery: {
    icon: PackageCheck,
    title: () => `Calibration completed — ready for collection`,
    body: (p) => `${p.description || "Item"}${p.serial_number ? ` · ${p.serial_number}` : ""}`,
    path: "/app/portal",
    tone: "green",
  },
  equipment_dispatched: {
    icon: Truck,
    title: () => `Equipment dispatched`,
    body: (p) => `${p.description || "Item"}${p.delivery_note_number ? ` · DN ${p.delivery_note_number}` : ""}`,
    path: "/app/portal",
    tone: "blue",
  },
  equipment_scrapped_customer: {
    icon: Trash2,
    title: () => `Equipment marked unserviceable`,
    body: (p) => `${p.description || "Item"} — ${p.reason || ""}`,
    path: "/app/portal",
    tone: "red",
  },
};

const TONE_BG = {
  amber: "#fff4d6", green: "#dff6e3", red: "#fde2e2", blue: "#e1edff",
};
const TONE_FG = {
  amber: "#8a5a00", green: "#1d6a3a", red: "#a32424", blue: "#1d4ed8",
};

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell({ dark = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = React.useState(false);
  const [unread, setUnread] = React.useState(0);
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const ref = React.useRef(null);

  const fetchCount = React.useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread-count");
      setUnread(data?.count || 0);
    } catch {
      /* silent */
    }
  }, []);

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await api.get("/notifications", { params: { limit: 25 } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setErr(apiErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, POLL_MS);
    return () => clearInterval(t);
  }, [fetchCount]);

  React.useEffect(() => {
    if (open) fetchList();
  }, [open, fetchList]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const markRead = async (n) => {
    if (n.read) return;
    setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    setUnread((c) => Math.max(0, c - 1));
    try {
      await api.post(`/notifications/${n.id}/read`);
    } catch {
      /* revert silently — next poll corrects */
    }
  };

  const markAll = async () => {
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setUnread(0);
    try {
      await api.post("/notifications/mark-all-read");
    } catch {
      /* silent */
    }
  };

  const handleOpen = (n) => {
    markRead(n);
    const meta = KIND_META[n.kind];
    setOpen(false);
    if (meta?.path) navigate(meta.path);
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        className="icon-btn"
        title="Notifications"
        data-testid="notifications-bell"
        onClick={() => setOpen((v) => !v)}
        style={{ position: "relative" }}
      >
        <Bell size={18} />
        {unread > 0 && (
          <span
            data-testid="notifications-unread-badge"
            style={{
              position: "absolute", top: 2, right: 2,
              minWidth: 16, height: 16, padding: "0 4px",
              borderRadius: 8, background: "#e11d48", color: "#fff",
              fontSize: 10, fontWeight: 700,
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              border: dark ? "1.5px solid #1c1b18" : "1.5px solid #fff",
              lineHeight: 1,
            }}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notifications-panel"
          style={{
            position: "absolute", top: "calc(100% + 8px)", right: 0,
            width: 380, maxHeight: 520, overflow: "hidden",
            background: "#fff", color: "#1c1b18",
            border: "1px solid #e5e3dc", borderRadius: 12,
            boxShadow: "0 12px 32px rgba(20,20,20,.18)",
            display: "flex", flexDirection: "column", zIndex: 50,
          }}
        >
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "12px 14px", borderBottom: "1px solid #efece4",
          }}>
            <div style={{ fontWeight: 700, fontSize: 14 }}>
              Notifications {unread > 0 && (
                <span style={{ color: "#9b9890", fontWeight: 500, marginLeft: 6 }}>
                  · {unread} unread
                </span>
              )}
            </div>
            <button
              data-testid="notifications-mark-all"
              className="icon-btn"
              onClick={markAll}
              disabled={unread === 0}
              title="Mark all as read"
              style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                fontSize: 12, fontWeight: 600, padding: "4px 8px",
                color: unread === 0 ? "#bfbcb2" : "#3a7a3a",
                cursor: unread === 0 ? "default" : "pointer",
                whiteSpace: "nowrap", flexShrink: 0,
              }}
            >
              <CheckCheck size={14} /> Mark all
            </button>
          </div>

          <div style={{ overflowY: "auto", flex: 1 }}>
            {loading && (
              <div style={{ padding: 18, color: "#86857d", fontSize: 13 }}>Loading…</div>
            )}
            {err && (
              <div style={{ padding: 18, color: "#a32424", fontSize: 13 }}>{err}</div>
            )}
            {!loading && !err && items.length === 0 && (
              <div data-testid="notifications-empty" style={{
                padding: "28px 18px", textAlign: "center",
                color: "#86857d", fontSize: 13,
              }}>
                You&apos;re all caught up.
              </div>
            )}
            {!loading && items.map((n) => {
              const meta = KIND_META[n.kind] || {};
              const Icon = meta.icon || Bell;
              const title = meta.title ? meta.title(n.payload || {}) : (n.kind || "Notification");
              const body = meta.body ? meta.body(n.payload || {}) : "";
              return (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => handleOpen(n)}
                  style={{
                    display: "flex", gap: 10, padding: "12px 14px",
                    width: "100%", textAlign: "left",
                    background: n.read ? "#fff" : "#fbf8ee",
                    borderBottom: "1px solid #f3f1e9",
                    cursor: "pointer", border: "none",
                  }}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8, flex: "0 0 auto",
                    background: TONE_BG[meta.tone] || "#f3f1e9",
                    color: TONE_FG[meta.tone] || "#5a5852",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <Icon size={16} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: n.read ? 500 : 700,
                      color: "#1c1b18",
                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      {title}
                    </div>
                    {body && (
                      <div style={{
                        fontSize: 12, color: "#5a5852", marginTop: 2,
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>
                        {body}
                      </div>
                    )}
                    <div style={{
                      fontSize: 11, color: "#9b9890", marginTop: 4,
                    }}>
                      {n.by_name ? `${n.by_name} · ` : ""}{timeAgo(n.created_at)}
                    </div>
                  </div>
                  {!n.read && (
                    <span style={{
                      width: 8, height: 8, borderRadius: 4,
                      background: "#e11d48", flex: "0 0 auto", marginTop: 12,
                    }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
